import { useEffect, useRef } from "react";
import { MdCancel } from "react-icons/md";
import { datadogLogs } from "@datadog/browser-logs";

import RoundLoader from "../../Hooks/RoudLoader.js";
import {
    useCameraPermission,
    CAMERA_PERMISSION_STATUS,
} from "../../Hooks/useCameraPermission.js";
import { getDeviceContext } from "../../Utils/deviceValidation.js";

/**
 * Wraps a scan flow and requests camera permission before rendering any of
 * it. If the permission prompt is denied or dismissed, no scan step (front
 * ID, back ID, selfie, document upload) is ever rendered — only an error.
 */
const CameraGate = ({ children, tenantColor, responseScan }) => {
    const status = useCameraPermission();
    const hasLoggedRef = useRef(false);

    useEffect(() => {
        if (status === CAMERA_PERMISSION_STATUS.CHECKING || hasLoggedRef.current) {
            return;
        }
        hasLoggedRef.current = true;

        if (status === CAMERA_PERMISSION_STATUS.DENIED) {
            datadogLogs.logger.error(
                `Camera permission denied, blocking scan flow (CameraGate) with the invitationId: ${responseScan?.invitation_id}`,
                {
                    invitationId: responseScan?.invitation_id,
                    message: "Camera permission denied or unavailable",
                    ...getDeviceContext(),
                }
            );
        } else {
            datadogLogs.logger.info(
                `Camera permission granted (CameraGate) with the invitationId: ${responseScan?.invitation_id}`,
                {
                    invitationId: responseScan?.invitation_id,
                    ...getDeviceContext(),
                }
            );
        }
    }, [status, responseScan]);

    if (status === CAMERA_PERMISSION_STATUS.CHECKING) {
        return (
            <div className="flex justify-center items-center min-h-screen flex-col gap-3 container px-3 py-5 bg-[#F8F7F6] w-full">
                <RoundLoader loading={true} color={tenantColor} />
            </div>
        );
    }

    if (status === CAMERA_PERMISSION_STATUS.DENIED) {
        return (
            <div className="flex justify-center items-center min-h-screen flex-col gap-3 container px-3 py-5 bg-[#F8F7F6] w-full">
                <MdCancel size={150} className="text-[#E43122]" />
                <h2 className="text-2xl text-[#E43122]">Error</h2>
                <p className="my-5 text-center text-sm">
                    Camera access is required to verify your identity.
                    <br />
                    Please allow camera access for this site and reload the
                    page to continue.
                </p>
            </div>
        );
    }

    return children;
};

export default CameraGate;
